import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Video, 
  Users, 
  Download,
  Shield,
  Globe,
  Smartphone,
  Camera,
  Clock,
  Star,
  Zap,
  Heart,
  Monitor,
  Wifi,
  Cloud,
  PlayCircle,
  Settings,
  BarChart3,
  Lock
} from 'lucide-react';

const LiveStreamFeatures = () => {
  const primaryFeatures = [
    {
      icon: Video,
      title: "Professional HD Streaming",
      description: "Broadcast in crystal-clear HD with adaptive bitrate streaming that automatically adjusts to each viewer's internet speed.",
      benefits: ["1080p HD quality", "Adaptive streaming", "Global CDN delivery", "Sub-15 second latency"]
    },
    {
      icon: Users,
      title: "Unlimited Family Viewing",
      description: "No limits on concurrent viewers. Grandparents in Florida, deployed parents overseas - everyone can watch simultaneously.",
      benefits: ["Unlimited viewers", "Multi-device support", "Global accessibility", "Family sharing tools"]
    },
    {
      icon: Download,
      title: "Instant Automatic Recording",
      description: "Every live stream is automatically recorded in HD and available for download within 60 seconds of ending.",
      benefits: ["Auto-recording", "HD downloads", "Cloud storage", "30-90 day access"]
    },
    {
      icon: Shield,
      title: "Military-Grade Security", 
      description: "Private, token-based access ensures only purchasing families can view your events. COPPA compliant.",
      benefits: ["Secure tokens", "Family-only access", "COPPA compliant", "Privacy controls"]
    }
  ];

  const advancedFeatures = [
    {
      icon: Globe,
      title: "Global Infrastructure",
      description: "Powered by Cloudflare's global network for lightning-fast streaming worldwide"
    },
    {
      icon: Smartphone,
      title: "Mobile Optimized",
      description: "Perfect viewing experience on phones, tablets, and computers"
    },
    {
      icon: Camera,
      title: "Multi-Camera Ready",
      description: "Support for multiple camera angles and professional switching"
    },
    {
      icon: Clock,
      title: "Scheduled Streaming",
      description: "Automated start/stop based on your event schedule"
    },
    {
      icon: BarChart3,
      title: "Real-Time Analytics",
      description: "Live viewer counts, engagement metrics, and revenue tracking"
    },
    {
      icon: Cloud,
      title: "Cloud Integration",
      description: "Seamlessly integrates with your existing HiveShare platform"
    },
    {
      icon: PlayCircle,
      title: "Instant Playback",
      description: "Recording available immediately after stream ends"
    },
    {
      icon: Settings,
      title: "Easy Setup",
      description: "10-minute setup with guided configuration and testing"
    },
    {
      icon: Lock,
      title: "Access Control",
      description: "Time-limited access, IP restrictions, and family management"
    }
  ];

  const comparisonData = [
    {
      feature: "Setup Time",
      hiveshare: "10 minutes",
      youtube: "Complex",
      zoom: "30+ minutes",
      traditional: "Hours"
    },
    {
      feature: "Cost per Event",
      hiveshare: "$25-75",
      youtube: "Free (with ads)",
      zoom: "$50-200",
      traditional: "$500+"
    },
    {
      feature: "Family Privacy",
      hiveshare: "Complete",
      youtube: "None",
      zoom: "Limited", 
      traditional: "Varies"
    },
    {
      feature: "Recording Quality",
      hiveshare: "HD Auto-Save",
      youtube: "YouTube compression",
      zoom: "Local only",
      traditional: "Extra cost"
    },
    {
      feature: "Viewer Limits",
      hiveshare: "Unlimited",
      youtube: "Unlimited*",
      zoom: "Plan dependent",
      traditional: "Very limited"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white mb-6 px-6 py-2 text-lg">
            <Star className="h-5 w-5 mr-2" />
            Enterprise-Grade Features
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Everything You Need for
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Perfect Events</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            Professional-grade live streaming technology designed specifically for activity centers and family experiences.
          </p>
        </motion.div>

        {/* Primary Features Grid */}
        <div className="grid md:grid-cols-2 gap-12 mb-20 max-w-7xl mx-auto">
          {primaryFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card className="h-full hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200">
                <CardContent className="p-8">
                  <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-4 w-fit mb-6">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">{feature.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center text-sm">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                        <span className="text-gray-700 font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Advanced Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advanced Capabilities
            </h3>
            <p className="text-xl text-gray-600">
              Professional features that set HiveShare Live apart from the competition
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {advancedFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 group hover:scale-105">
                  <CardContent className="p-6 text-center">
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3 w-fit mx-auto mb-4 group-hover:from-blue-100 group-hover:to-purple-100 transition-colors">
                      <feature.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-12">
            <Badge className="bg-gradient-to-r from-red-500 to-orange-500 text-white mb-6 px-6 py-2 text-lg">
              <Zap className="h-5 w-5 mr-2" />
              Competitive Comparison
            </Badge>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why HiveShare Live Wins
            </h3>
            <p className="text-xl text-gray-600">
              See how we compare to other streaming solutions for activity centers
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-gray-100">
            {/* Table Header */}
            <div className="grid grid-cols-5 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <div className="p-6">
                <h4 className="font-bold text-gray-900 text-lg">Feature</h4>
              </div>
              <div className="p-6 text-center bg-gradient-to-r from-blue-50 to-purple-50 border-x-2 border-blue-200">
                <h4 className="font-bold text-blue-900 text-lg">HiveShare Live</h4>
                <Badge className="bg-blue-100 text-blue-700 text-xs mt-2">
                  <Star className="h-3 w-3 mr-1" />
                  Best Choice
                </Badge>
              </div>
              <div className="p-6 text-center">
                <h4 className="font-bold text-gray-700">YouTube Live</h4>
              </div>
              <div className="p-6 text-center">
                <h4 className="font-bold text-gray-700">Zoom</h4>
              </div>
              <div className="p-6 text-center">
                <h4 className="font-bold text-gray-700">Traditional AV</h4>
              </div>
            </div>

            {/* Table Rows */}
            {comparisonData.map((row, index) => (
              <div key={index} className={`grid grid-cols-5 border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <div className="p-4">
                  <span className="font-medium text-gray-900">{row.feature}</span>
                </div>
                <div className="p-4 text-center bg-gradient-to-r from-blue-50 to-purple-50 border-x border-blue-200">
                  <span className="font-bold text-blue-900">{row.hiveshare}</span>
                </div>
                <div className="p-4 text-center">
                  <span className="text-gray-600">{row.youtube}</span>
                </div>
                <div className="p-4 text-center">
                  <span className="text-gray-600">{row.zoom}</span>
                </div>
                <div className="p-4 text-center">
                  <span className="text-gray-600">{row.traditional}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamFeatures;